import React, { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes as ReactRoutes } from 'react-router';

import RouteLoadingFallback from '../shared/RouteLoadingFallback';

const GlobalDiscover = lazy(() => import('../global-discover'));
import Navbar from './navbar/navbarV2';
import { usePermittedRoutes } from './routes/usePermittedRoutes';
import { isRouteValid } from './helpers';
import Layout from './layout';
import { AnalyticsRedirect, NavigateAndMergeQueryParams } from './redirects';
import SolveInsightsBrandFilterGuard from 'src/components/dashboard-pages/solve-insights/SolveInsightsBrandFilterGuard';
import SolveInsightsChannelGuard from 'src/components/dashboard-pages/solve-insights/SolveInsightsChannelGuard';
import { selectUserRole } from 'src/reducers/userReducer/userReducer';
import { stripBase } from 'src/utils/basePath';
import {
  DISCOVER_ROUTE_PREFIX,
  Routes,
  SOLVE_INSIGHT_ROUTE_PREFIX,
  UserRoles,
} from 'src/utils/enums';

const defaultNavbar = <Navbar />;

interface AppRoutesProps {
  permissionKeys: string[];
  permissionsLoaded: boolean;
}

const GlobalComponentByPath = ({ path }: { path: `${Routes}` }) => {
  if (
    path.startsWith(DISCOVER_ROUTE_PREFIX) ||
    path.startsWith(SOLVE_INSIGHT_ROUTE_PREFIX)
  ) {
    return (
      <Suspense fallback={null}>
        <GlobalDiscover />
      </Suspense>
    );
  }

  return null;
};

const RouteMain = ({
  Component,
  path,
  showNavBar,
}: {
  Component: React.ComponentType;
  path: `${Routes}`;
  showNavBar: boolean;
}) => {
  const content = <Component />;

  if (!path.startsWith(SOLVE_INSIGHT_ROUTE_PREFIX)) {
    return content;
  }

  return (
    <SolveInsightsBrandFilterGuard shouldHydrateCurrentUser={!showNavBar}>
      <SolveInsightsChannelGuard path={path}>
        {content}
      </SolveInsightsChannelGuard>
    </SolveInsightsBrandFilterGuard>
  );
};

const AppRoutes: React.FC<React.PropsWithChildren<AppRoutesProps>> = ({
  permissionKeys,
  permissionsLoaded,
}) => {
  const role = useSelector(selectUserRole);
  const permittedRoutes = usePermittedRoutes({
    permissionKeys,
    role,
  });
  const permissionSet = new Set(permissionKeys);
  const routes = permittedRoutes
    .map(route => {
      if (!route) {
        return null;
      }
      const { path, showNavBar = true, showSideBar = true } = route;

      return (
        <Route
          element={
            <Layout
              globalComponent={<GlobalComponentByPath path={path} />}
              main={
                <Suspense fallback={<RouteLoadingFallback />}>
                  <RouteMain
                    Component={route.component}
                    path={path}
                    showNavBar={showNavBar}
                  />
                </Suspense>
              }
              navbar={showNavBar ? defaultNavbar : null}
              permissions={permissionSet}
              showMainApp
              showSideBar={showSideBar}
            />
          }
          key={path}
          path={path}
        />
      );
    })
    .filter(Boolean);

  const path = stripBase(window.location.pathname);
  const validPaths = Object.values(Routes).filter(item =>
    permittedRoutes.find(route => route?.path === item),
  );
  const isInitialPermittedRoutes = permittedRoutes.length <= 1;
  const validRoute = isRouteValid(path, validPaths);
  const navigateToHome =
    !validRoute && !isInitialPermittedRoutes && permissionsLoaded;

  if (role === UserRoles.ROLE_AGENT) {
    return <ReactRoutes>{routes}</ReactRoutes>;
  }

  return (
    <>
      {navigateToHome && <Navigate replace to={Routes.ACCOUNT_SETTINGS} />}
      <ReactRoutes>
        <Route
          element={
            <NavigateAndMergeQueryParams
              newParams={{ tab: 'overview' }}
              to={Routes.DISCOVER_INSIGHTS}
            />
          }
          path={Routes.DISCOVER_DASHBOARD}
        />
        <Route
          element={
            <NavigateAndMergeQueryParams
              newParams={{ tab: 'topics' }}
              to={Routes.DISCOVER_INSIGHTS}
            />
          }
          path={Routes.DISCOVER_TOPICS}
        />
        <Route
          element={<AnalyticsRedirect />}
          path='/analytics-beta/:dashboardId'
        />
        <Route
          element={
            <NavigateAndMergeQueryParams
              newParams={{ channel: 'email' }}
              to={Routes.SOLVE_CONFIGURATION}
            />
          }
          path={Routes.SOLVE_EMAIL_CONFIGURATION}
        />
        <Route
          element={
            <NavigateAndMergeQueryParams
              newParams={{ channel: 'widget' }}
              to={Routes.SOLVE_CONFIGURATION}
            />
          }
          path={Routes.WIDGET_CONFIGURATION}
        />
        {routes}
      </ReactRoutes>
    </>
  );
};

export default AppRoutes;
